import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { printAnalysis } from '../state/analysis.actions';
import { selectLoadingPrintAnalysis } from '../state/analysis.selectors';
import { PrintForm } from '../models/PrintForm';

@Component({
    selector: 'print',
    templateUrl: './print.component.html',
    styleUrls: ['./print.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PrintComponent {
    @Output() closeDialogEvent = new EventEmitter();

    @Input() printAnalysisId: number | null = null;

    $selectLoadingPrintAnalysis = this.store.select(selectLoadingPrintAnalysis);

    printForm: FormGroup = this.formBuilder.group({
        analysis: [null],
        analysisId: [null],
        filename: ['sera-report-' + Date.now() + '.pdf'],
        language: [null],
        multipleFeatures: ['multipleFeatures'],
        selectAll: [false],
        coverSheet: [true],
        statistic: [false],
        rzStatistic: [false],
        ergomanStatistics: [false],
        measuresStatistics: [false],
        features: [false],
        clusterPosture: [false],
        clusterWorkingEnvironment: [false],
        clusterMentalStress: [false],
        clusterAccidents: [false],
        historicalDevelopment: [false],
        measuresList: [false],
        riskAssessment: [false],
        featureCompact13: [false]
    });

    printOptions: PrintOption[] = [
        {
            name: 'PAGE.ANALYSIS.PRINT_PARAMS.SELECT_ALL.LBL',
            key: 'selectAll'
        },
        {
            name: 'PAGE.ANALYSIS.PRINT_PARAMS.COVER_SHEET.LBL',
            key: 'coverSheet'
        },
        {
            name: 'PAGE.ANALYSIS.PRINT_PARAMS.STATISTICS.LBL',
            key: 'statistic',
            hasSubOption: true,
            subOption: [
                {
                    name: 'PAGE.ANALYSIS.PRINT_PARAMS.RZ_STATISTICS.LBL',
                    key: 'rzStatistic'
                },
                {
                    name: 'PAGE.ANALYSIS.PRINT_PARAMS.ERGOMAN_STATISTICS.LBL',
                    key: 'ergomanStatistics'
                },
                {
                    name: 'PAGE.ANALYSIS.PRINT_PARAMS.MEASURES_STATISTICS.LBL',
                    key: 'measuresStatistics'
                }
            ]
        },
        {
            name: 'PAGE.ANALYSIS.PRINT_PARAMS.FEATURES.LBL',
            key: 'features',
            hasSubOption: true,
            subOption: [
                {
                    name: 'PAGE.REPORT.MM_01.PHYSICAL_CLUSTER.LBL',
                    key: 'clusterPosture'
                },
                {
                    name: 'PAGE.ANALYSIS.PRINT_PARAMS.CLUSTER_WORKING_ENVIRONMENT.LBL',
                    key: 'clusterWorkingEnvironment'
                },
                {
                    name: 'PAGE.ANALYSIS.PRINT_PARAMS.CLUSTER_MENTAL_STRESS.LBL',
                    key: 'clusterMentalStress'
                },
                {
                    name: 'PAGE.ANALYSIS.PRINT_PARAMS.CLUSTER_ACCIDENTS.LBL',
                    key: 'clusterAccidents'
                }
            ]
        },
        {
            name: 'PAGE.ANALYSIS.PRINT_PARAMS.HISTORICAL_DEVELOPMENT.LBL',
            key: 'historicalDevelopment'
        },
        {
            name: 'PAGE.ANALYSIS.PRINT_PARAMS.MEASURES_LIST.LBL',
            key: 'measuresList'
        },
        {
            name: 'PAGE.ANALYSIS.PRINT_PARAMS.RISK_ASSESSMENT.LBL',
            key: 'riskAssessment'
        },
        {
            name: 'PAGE.MERKMAL_13_COMPACT.LBL',
            key: 'featureCompact13'
        }
    ];

    printOptionsOnlyImportant: PrintOption[] = this.printOptions.filter(option => option.key !== 'selectAll' && option.key !== 'coverSheet');

    multipleCriteriaOptions: { name: string; key: string }[] = [
        {
            name: 'PAGE.ANALYSIS.PRINT_PARAMS.MOTOR_CASES.LBL',
            key: 'multipleFeatures'
        },
        {
            name: 'PAGE.ANALYSIS.PRINT_PARAMS.RZ_LIGHT_PRINT.LBL',
            key: 'noMultipleFeatures'
        }
    ];

    constructor(private readonly formBuilder: NonNullableFormBuilder, private readonly store: Store) {}

    closeDialog(): void {
        this.closeDialogEvent.emit();
        this.printForm.reset();
    }

    print(): void {
        const printForm: PrintForm = {
            ...this.printForm.value,
            multipleFeatures: this.printForm.controls['multipleFeatures'].value === 'multipleFeatures'
        };
        if (this.printAnalysisId !== null) {
            printForm.analysisId = this.printAnalysisId;
        }
        this.store.dispatch(printAnalysis({ print: printForm }));
        this.closeDialog();
    }

    setSelectAllCheckbox(): void {
        this.printForm.controls['selectAll'].setValue(
            this.printOptionsOnlyImportant.every(printOption => this.printForm.controls[printOption.key].value === true)
        );
    }

    setChildCheckboxes(option: PrintOption, value: boolean): void {
        option.subOption?.forEach(subOption => this.printForm.controls[subOption.key].setValue(value));
    }

    checkboxParentChanged(event: boolean, option: PrintOption) {
        if (option.key === 'selectAll') {
            this.printOptionsOnlyImportant.forEach(printOption => {
                this.printForm.controls[printOption.key].setValue(event);
                if (printOption.hasSubOption) {
                    this.setChildCheckboxes(printOption, event);
                }
            });
        } else {
            this.printForm.controls[option.key].setValue(event);
            if (option.hasSubOption) {
                this.setChildCheckboxes(option, event);
            }
        }
        this.setSelectAllCheckbox();
    }

    checkboxChildChanged(event: boolean, option: PrintOption, subOption: PrintOption) {
        this.printForm.controls[subOption.key].setValue(event);
        this.printForm.controls[option.key].setValue(option.subOption?.every(subOption => this.printForm.controls[subOption.key].value === true));
        this.setSelectAllCheckbox();
    }
}

export type PrintOption = {
    name: string;
    key: string;
    hasSubOption?: boolean;
    subOption?: PrintOption[];
};
